/**
 * App Routes
 *
 * All page-level components are loaded lazily via React.lazy() so each module
 * group is bundled into its own JS chunk and fetched only when first navigated
 * to.  The single <Suspense> wrapper in AppRoutes shows a spinner while any
 * chunk loads.
 *
 * Keep-eager (never lazy):
 *   - DashboardLayout, PrivateRouteWrapper, PublicRouteWrapper, AdminRoute — must
 *     render before any route resolves.
 *   - CampaignsProvider — context that wraps child routes.
 *   - LoadingSpinner, ComingSoon — used as Suspense fallback / placeholder.
 *   - BeatPageLayout, FoundationsRunLayout, PersuasionLabBuildLayout — nested
 *     route layout wrappers; lazifying them would flash the spinner on every
 *     child-route navigation within their section.
 */

import ComingSoon from '@/components/shared/ComingSoon'
import LoadingSpinner from '@/components/ui/loading'
import { CampaignsProvider } from '@/context/campaigns'
import AdminRoute from '@/hoc/AdminRoute'
import EmailVerifiedRouteWrapper from '@/hoc/EmailVerifiedRouteWrapper'
import PrivateRouteWrapper from '@/hoc/PrivateRouteWrapper'
import PublicRouteWrapper from '@/hoc/PublicRouteWrapper'
import SubscriptionRouteWrapper from '@/hoc/SubscriptionRouteWrapper'
import DashboardLayout from '@/layout/DashboardLayout'
import MobileConsumerDeliverableLandingPage from '@/modules/mobile-consumer/MobileConsumerDeliverableLandingPage'
import { useAuthContext } from '@/context'
// Layout wrappers kept eager — see file-level comment above
import { BeatPageLayout } from '@/modules/campaigns/components/BeatPageLayout'
import { FoundationsRunLayout } from '@/modules/persuasion-lab/components/FoundationsRunLayout'
import { PersuasionLabBuildLayout } from '@/modules/persuasion-lab/components/PersuasionLabBuildLayout'
import { lazy, Suspense, type ComponentType } from 'react'
import { Navigate, Route, Routes } from 'react-router'

/**
 * Vite HMR can briefly invalidate lazy chunk URLs (stale `?t=`); the browser then
 * throws "Failed to fetch dynamically imported module". One retry in dev avoids
 * a full refresh, especially for very large admin chunks.
 */
function lazyImportWithDevRetry<T extends ComponentType<unknown>>(loader: () => Promise<{ default: T }>) {
    return lazy(async () => {
        try {
            return await loader()
        } catch (e) {
            if (!import.meta.env.DEV) throw e
            await new Promise<void>(resolve => setTimeout(resolve, 200))
            return await loader()
        }
    })
}

// ---------------------------------------------------------------------------
// Admin pages — default exports, lazy-compatible directly
// ---------------------------------------------------------------------------
const AdminBundle = lazy(() => import('@/modules/admin/bundles/AdminBundle'))
const AdminBundles = lazy(() => import('@/modules/admin/bundles/AdminBundles'))
const AdminBusinesses = lazy(() => import('@/modules/admin/businesses/Businesses'))
const AdminComzoneCode = lazy(() => import('@/modules/admin/comzone-codes/AdminComzoneCode'))
const AdminComzoneCodes = lazy(() => import('@/modules/admin/comzone-codes/AdminComzoneCodes'))
const AdminDashboard = lazy(() => import('@/modules/admin/dashboard/dashboard'))
const AdminHome = lazy(() => import('@/modules/admin/dashboard/home'))
const AdminDatasetTemplate = lazy(() => import('@/modules/admin/dataset-templates/AdminDatasetTemplate'))
const AdminDatasetTemplates = lazy(() => import('@/modules/admin/dataset-templates/AdminDatasetTemplates'))
const AdminFlowEntities = lazy(() => import('@/modules/admin/entities/entities'))
const AdminFlowEntity = lazy(() => import('@/modules/admin/entities/entity'))
const AdminFreebies = lazy(() => import('@/modules/admin/freebies/AdminFreebies'))
const FreebieRunAuditPage = lazy(() => import('@/modules/admin/freebies/FreebieRunAudit'))
const InterviewPacksAdmin = lazy(() => import('@/modules/admin/interview-packs/InterviewPacksAdmin'))
const DeliverableTemplatesAdmin = lazy(() => import('@/modules/admin/deliverable-templates/DeliverableTemplatesAdmin'))
const OutcomeCatalogAdmin = lazy(() => import('@/modules/admin/outcome-catalog/OutcomeCatalogAdmin'))
const AdminGAReportTemplates = lazy(() => import('@/modules/admin/ga-report-templates/AdminGAReportTemplates'))
const DeliverableStudioAudit = lazy(() =>
    import('@/modules/admin/deliverable-studio-audit/DeliverableStudioAudit').then(m => ({ default: m.default }))
)
const TwoHQReportTemplatesPage = lazy(() => import('@/modules/two-hq/pages/TwoHQReportTemplatesPage'))
const TwoHQGAImportsPage = lazy(() => import('@/modules/two-hq/pages/TwoHQGAImportsPage'))
const TwoHQReportsPage = lazy(() => import('@/modules/two-hq/pages/TwoHQReportsPage'))
const AdminAudit = lazyImportWithDevRetry(() => import('@/modules/admin/audit/AdminAudit'))
const InterviewVariablesAdmin = lazy(() => import('@/modules/admin/interview-variables/InterviewVariablesAdmin'))
const IdentityRegistryAdmin = lazy(() => import('@/modules/admin/identity-registry/IdentityRegistryAdmin'))
const BundleManagerAdmin = lazy(() => import('@/modules/admin/bundles/BundleManagerAdmin').then(m => ({ default: m.BundleManagerAdmin })))
const AppStoreSetupAdmin = lazy(() => import('@/modules/admin/mobile-store/AppStoreSetupAdmin'))
const MobileUsersAdmin = lazy(() => import('@/modules/admin/mobile-users/MobileUsersAdmin'))
const CampaignScenariosAdmin = lazy(() => import('@/modules/admin/campaign-scenarios/CampaignScenariosAdmin'))
const Prompts = lazy(() => import('@/modules/admin/prompts/prompts'))
const SectionTypeDetail = lazy(() => import('@/modules/admin/section-types/SectionTypeDetail'))
const SectionTypesList = lazy(() => import('@/modules/admin/section-types/SectionTypesList'))
const Template = lazy(() => import('@/modules/admin/template/template'))
const Templates = lazy(() => import('@/modules/admin/template/templates'))
const AdminUsers = lazy(() => import('@/modules/admin/users/users'))
const CreditAuditAdmin = lazy(() => import('@/modules/admin/credit-audit/CreditAuditAdmin'))
const AdminWizardCategories = lazy(() => import('@/modules/admin/wizard-categories/categories'))
const AdminWizardCategory = lazy(() => import('@/modules/admin/wizard-categories/category'))
const AdminWizardFormsBuilder = lazy(() => import('@/modules/admin/wizard-forms-builder/AdminWizardFormsBuilder'))
const MultiStepFormBuilder = lazy(() => import('@/modules/admin/wizard-forms-builder/ManageWizardForms'))
const AdminSequence = lazy(() => import('@/modules/admin/workflows/AdminSequence'))
const AdminSequences = lazy(() => import('@/modules/admin/workflows/AdminSequences'))
const TargetGroupReport = lazy(() => import('@/modules/admin/workflows/report/target-group'))

// Admin — named exports: use .then(m => ({ default: m.X })) wrapper
const PhDPromptManagement = lazy(() =>
    import('@/modules/admin/visual-engine/PhDPromptManagement').then(m => ({ default: m.PhDPromptManagement }))
)
const SeedImageGallery = lazy(() =>
    import('@/modules/admin/visual-engine/SeedImageGallery').then(m => ({ default: m.SeedImageGallery }))
)

// ---------------------------------------------------------------------------
// Auth pages — default exports
// ---------------------------------------------------------------------------
const AcceptInvitation = lazy(() => import('@/modules/auth/accept-invitation'))
const ForgotPassword = lazy(() => import('@/modules/auth/forgot-password'))
const Login = lazy(() => import('@/modules/auth/login'))
const LogoutUser = lazy(() => import('@/modules/auth/logout'))
const MagicLinkLogin = lazy(() => import('@/modules/auth/magic-link-login'))
const Register = lazy(() => import('@/modules/auth/register'))
const ResetPassword = lazy(() => import('@/modules/auth/reset-password'))
const SetPassword = lazy(() => import('@/modules/auth/set-password'))
const SetupDemo = lazy(() => import('@/modules/auth/setup-demo'))
const VerifyEmail = lazy(() => import('@/modules/auth/verify-email'))
const PublicInterviewPage = lazy(() => import('@/pages/PublicInterviewPage'))
const CompanionViewPage = lazy(() => import('@/pages/CompanionViewPage'))
const MobileInterviewDeepLinkPage = lazy(() => import('@/pages/MobileInterviewDeepLinkPage'))
const MobileInterviewDeliverablesDeepLinkPage = lazy(() => import('@/pages/MobileInterviewDeliverablesDeepLinkPage'))

// ---------------------------------------------------------------------------
// Brand / voice
// ---------------------------------------------------------------------------
const BrandGuides = lazy(() => import('@/modules/brand-guide/BrandGuides'))
const ManageBrandGuide = lazy(() => import('@/modules/brand-guide/components/ManageBrandGuide'))
const BrandVoices = lazy(() => import('@/modules/brand-voices/BrandVoices'))

// ---------------------------------------------------------------------------
// Campaigns — barrel import (named exports via .then wrapper).
// All these point at the same barrel so Vite produces a single campaigns chunk.
// ---------------------------------------------------------------------------
const ALOInventory = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.ALOInventory })))
const AssetsBuilder = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.AssetsBuilder })))
const BaitBuilder = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.BaitBuilder })))
const BrandGuidelines = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.BrandGuidelines })))
const CampaignAuditPage = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.CampaignAuditPage })))
const CampaignCommandCenter = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.CampaignCommandCenter }))
)
const CampaignDebugPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.CampaignDebugPage }))
)
const CampaignDetail = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.CampaignDetail })))
const DeliverableStudioPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.DeliverableStudioPage }))
)
const CampaignsDashboard = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.CampaignsDashboard }))
)
const ClaimsLibrary = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.ClaimsLibrary })))
const CompanyDetailPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.CompanyDetailPage }))
)
const CorridorBuilder = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.CorridorBuilder })))
const CreativeMatrix = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.CreativeMatrix })))
const DatasetManager = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.DatasetManager })))
const DripScheduler = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.DripScheduler })))
const EvidenceLibrary = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.EvidenceLibrary })))
const FanOutGenerator = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.FanOutGenerator })))
const FieldMapper = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.FieldMapper })))
const FrameworkCanvas = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.FrameworkCanvas })))
const GoalLibrary = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.GoalLibrary })))
const HookLibrary = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.HookLibrary })))
const IntentDiscovery = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.IntentDiscovery })))
const LibraryBrandAssetsPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.LibraryBrandAssetsPage }))
)
const HeftProfilesPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.HeftProfilesPage }))
)
const HeftTargetSheetPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.HeftTargetSheetPage }))
)
const HeftSubjectPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.HeftSubjectPage }))
)
const PersonPortraitPortalPage = lazy(() =>
    import('@/modules/portraits/pages').then(m => ({ default: m.PersonPortraitPortalPage }))
)
const CompanyPortraitPortalPage = lazy(() =>
    import('@/modules/portraits/pages').then(m => ({ default: m.CompanyPortraitPortalPage }))
)
const ProductPortraitPortalPage = lazy(() =>
    import('@/modules/portraits/pages').then(m => ({ default: m.ProductPortraitPortalPage }))
)
const PortraitPeopleIndexPage = lazy(() =>
    import('@/modules/portraits/pages').then(m => ({ default: m.PortraitPeopleIndexPage }))
)
const PortraitCompaniesIndexPage = lazy(() =>
    import('@/modules/portraits/pages').then(m => ({ default: m.PortraitCompaniesIndexPage }))
)
const PortraitProductsIndexPage = lazy(() =>
    import('@/modules/portraits/pages').then(m => ({ default: m.PortraitProductsIndexPage }))
)
const PortraitDashboardPage = lazy(() => import('@/modules/portraits/pages').then(m => ({ default: m.PortraitDashboardPage })))
const PortraitPeopleStudioPage = lazy(() =>
    import('@/modules/portraits/pages').then(m => ({ default: m.PortraitPeopleStudioPage }))
)
const PortraitCompaniesStudioPage = lazy(() =>
    import('@/modules/portraits/pages').then(m => ({ default: m.PortraitCompaniesStudioPage }))
)
const PortraitProductsStudioPage = lazy(() =>
    import('@/modules/portraits/pages').then(m => ({ default: m.PortraitProductsStudioPage }))
)
const InterviewPacksPage = lazy(() =>
    import('@/modules/interview-packs').then(m => ({ default: m.InterviewPacksPage }))
)
const InterviewPackEditor = lazy(() =>
    import('@/modules/interview-packs').then(m => ({ default: m.InterviewPackEditor }))
)
const ContentStrategyPackPage = lazy(() => import('@/modules/campaigns/pages/ContentStrategyPackPage.tsx'))
const LibraryCompaniesPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.LibraryCompaniesPage }))
)
const IdentityEngineSetupWizard = lazy(() =>
    import('@/modules/campaigns/pages/IdentityEngineSetupWizard').then(m => ({ default: m.IdentityEngineSetupWizard }))
)
const LibraryPeoplePage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.LibraryPeoplePage }))
)
const LibraryProductsPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.LibraryProductsPage }))
)
const MegaAssetBuilder = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.MegaAssetBuilder })))
const Newsroom = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.Newsroom })))
const Outreach = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.Outreach })))
const PersonDetailPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.PersonDetailPage }))
)
const PillarBuilder = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.PillarBuilder })))
const ProductDetailPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.ProductDetailPage }))
)
const PRStoryLibrary = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.PRStoryLibrary })))
const StoryArchitecture = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.StoryArchitecture }))
)
const FoundationSummaryPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.FoundationSummaryPage }))
)
const StoryMap = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.StoryMap })))
const StoryReadbackPage = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.StoryReadbackPage }))
)
const StrategicOverview = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.StrategicOverview }))
)
const StrategyPage = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.StrategyPage })))
const StrategyReview = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.StrategyReview })))
const TargetEdit = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.TargetEdit })))
const TargetLibrary = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.TargetLibrary })))
const TargetSelection = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.TargetSelection })))
const TFDLibrary = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.TFDLibrary })))
const ThinkFeelDoBuilder = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.ThinkFeelDoBuilder }))
)
const TopicBuilder = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.TopicBuilder })))
const VariationGenerator = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.VariationGenerator }))
)
const VisualProfilesLibrary = lazy(() =>
    import('@/modules/campaigns/pages').then(m => ({ default: m.VisualProfilesLibrary }))
)
const VoiceDialIn = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.VoiceDialIn })))
const YourStory = lazy(() => import('@/modules/campaigns/pages').then(m => ({ default: m.YourStory })))

// Campaigns — direct named exports (not in barrel)
const CreateCampaignPage = lazy(() =>
    import('@/modules/campaigns/pages/CreateCampaignPage').then(m => ({ default: m.CreateCampaignPage }))
)
const TopicBankSimple = lazy(() => import('@/modules/campaigns/pages/TopicBankSimple'))

// Campaigns — landing page sub-barrel (named exports)
const LandingPageContentPage = lazy(() =>
    import('@/modules/campaigns/pages/LandingPage').then(m => ({ default: m.LandingPageContentPage }))
)
const LandingPageGoalsPage = lazy(() =>
    import('@/modules/campaigns/pages/LandingPage').then(m => ({ default: m.LandingPageGoalsPage }))
)
const LandingPagePreviewPage = lazy(() =>
    import('@/modules/campaigns/pages/LandingPage').then(m => ({ default: m.LandingPagePreviewPage }))
)
const LandingPageVisualPage = lazy(() =>
    import('@/modules/campaigns/pages/LandingPage').then(m => ({ default: m.LandingPageVisualPage }))
)

// Campaigns — influencer pages (direct named exports)
const BriefPackage = lazy(() =>
    import('@/modules/campaigns/pages/influencer/BriefPackage').then(m => ({ default: m.BriefPackage }))
)
const ImportStoryboardPage = lazy(() =>
    import('@/modules/campaigns/pages/influencer/ImportStoryboardPage').then(m => ({ default: m.ImportStoryboardPage }))
)
const CreatorSetupPage = lazy(() =>
    import('@/modules/campaigns/pages/influencer/CreatorSetupPage').then(m => ({ default: m.CreatorSetupPage }))
)
const MomentsPage = lazy(() =>
    import('@/modules/campaigns/pages/influencer/MomentsPage').then(m => ({ default: m.MomentsPage }))
)
// HEFT Campaign Step Pages
const HeftStorySelectionPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftStorySelectionPage').then(m => ({ default: m.HeftStorySelectionPage }))
)
const HeftIdentitySnapshotPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftIdentitySnapshotPage').then(m => ({ default: m.HeftIdentitySnapshotPage }))
)
const HeftCaseStudyPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftCaseStudyPage').then(m => ({ default: m.HeftCaseStudyPage }))
)
const HeftDeliverablesPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftDeliverablesPage').then(m => ({ default: m.HeftDeliverablesPage }))
)
// HEFT Track B Campaign Pages
const HeftContentStrategyPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftContentStrategyPage').then(m => ({ default: m.HeftContentStrategyPage }))
)
const HeftBeaconsPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftBeaconsPage').then(m => ({ default: m.HeftBeaconsPage }))
)
const HeftTFDPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftTFDPage').then(m => ({ default: m.HeftTFDPage }))
)
const HeftMoralValuesPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftMoralValuesPage').then(m => ({ default: m.HeftMoralValuesPage }))
)
const HeftPRDeliverablesPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftPRDeliverablesPage').then(m => ({ default: m.HeftPRDeliverablesPage }))
)
const HeftRecruitingDeliverablesPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftRecruitingDeliverablesPage').then(m => ({ default: m.HeftRecruitingDeliverablesPage }))
)
const HeftSalesDeliverablesPage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftSalesDeliverablesPage').then(m => ({ default: m.HeftSalesDeliverablesPage }))
)
const HeftStarDeepDivePage = lazy(() =>
    import('@/modules/campaigns/pages/heft/HeftStarDeepDivePage').then(m => ({ default: m.HeftStarDeepDivePage }))
)
const ScriptBuilder = lazy(() =>
    import('@/modules/campaigns/pages/influencer/ScriptBuilder').then(m => ({ default: m.ScriptBuilder }))
)
const Storyboard = lazy(() =>
    import('@/modules/campaigns/pages/influencer/Storyboard').then(m => ({ default: m.Storyboard }))
)

// ---------------------------------------------------------------------------
// Chat, dashboard, misc
// ---------------------------------------------------------------------------
const NewBusiness = lazy(() => import('@/modules/dashboard/new-business'))
const PipelineDashboard = lazy(() =>
    import('@/modules/home/PipelineDashboard').then(m => ({ default: m.PipelineDashboard }))
)
const PlaybooksPage = lazy(() =>
    import('@/modules/home/PlaybooksPage').then(m => ({ default: m.PlaybooksPage }))
)

// ---------------------------------------------------------------------------
// Datasets
// ---------------------------------------------------------------------------
const DatasetDetail = lazy(() => import('@/modules/datasets/DatasetDetail'))
const DatasetsRoot = lazy(() => import('@/modules/datasets/DatasetsRoot'))

// ---------------------------------------------------------------------------
// Document editor
// ---------------------------------------------------------------------------
const DocumentEditorContainer = lazy(() => import('@/modules/document-editor/document-editor-container'))

// ---------------------------------------------------------------------------
// Flows / wizards
// ---------------------------------------------------------------------------
const ExecutionDetailsPage = lazy(() => import('@/modules/flows/ExecutionDetailsPage'))
const MyWorkflows = lazy(() => import('@/modules/flows/MyWorkflows'))
const Results = lazy(() => import('@/modules/flows/Results'))
const MyFlowExecutionPage = lazy(() => import('@/modules/flows/components/MyFlowExecutionPage'))
const Cart = lazy(() => import('@/modules/flows/flows-store/Cart'))
const Checkout = lazy(() => import('@/modules/flows/flows-store/Checkout'))
const WizardStore = lazy(() => import('@/modules/flows/flows-store/WizardStore'))
const WizardDetailsPage = lazy(() => import('@/modules/flows/flows-store/flow-details/WizardDetailsPage'))

// ---------------------------------------------------------------------------
// Folders, forms, help, media
// ---------------------------------------------------------------------------
const Folder = lazy(() => import('@/modules/folders/folder'))
const Folders = lazy(() => import('@/modules/folders/folders'))
const CompletedFormResponse = lazy(() => import('@/modules/forms/CompletedFormResponse'))
const CompletedForms = lazy(() => import('@/modules/forms/CompletedForms'))
const Form = lazy(() => import('@/modules/forms/Form'))
const FormRoot = lazy(() => import('@/modules/forms/FormRoot'))
const FormSubmission = lazy(() => import('@/modules/forms/FormSubmission'))
const HeftHelpPage = lazy(() =>
    import('@/modules/help/HeftHelpPage').then(m => ({ default: m.HeftHelpPage }))
)
const HelpPage = lazy(() => import('@/modules/help/HelpPage'))
const MobileHelpPage = lazy(() => import('@/modules/mobile-consumer/MobileHelpPage'))
const MediaAssets = lazy(() => import('@/modules/media/MediaAssets'))
const MediaFolder = lazy(() => import('@/modules/media/MediaFolder'))

const HelpEntryPage = () => {
    const { user } = useAuthContext()
    if (user?.experience_variant === 'mobile_consumer') return <MobileHelpPage />
    return <HelpPage />
}

// ---------------------------------------------------------------------------
// Persona builder (named export)
// ---------------------------------------------------------------------------
const PersonaBuilderPage = lazy(() =>
    import('@/modules/persona/PersonaBuilderPage').then(m => ({ default: m.PersonaBuilderPage }))
)

// ---------------------------------------------------------------------------
// Persuasion Lab pages — barrel import (named exports via .then wrapper).
// All these point at the same barrel so Vite produces a single pl chunk.
// ---------------------------------------------------------------------------
const PersuasionBoardDetailPage = lazy(() =>
    import('@/modules/persuasion-lab/pages/PersuasionBoardDetailPage').then(m => ({
        default: m.PersuasionBoardDetailPage,
    }))
)
const ArgumentBuilderALOFreezePage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderALOFreezePage }))
)
const ArgumentBuilderClaimStructurePage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderClaimStructurePage }))
)
const ArgumentBuilderEncounterPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderEncounterPage }))
)
const ArgumentBuilderLockCasePage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderLockCasePage }))
)
const ArgumentBuilderObjectionsPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderObjectionsPage }))
)
const ArgumentBuilderOutcomesPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderOutcomesPage }))
)
const ArgumentBuilderPillarsPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderPillarsPage }))
)
const ArgumentBuilderProfileLockPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderProfileLockPage }))
)
const SituationsDashboard = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.SituationsDashboard }))
)
const ArgumentBuilderSituationsPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderSituationsPage }))
)
const ArgumentBuilderStressTestPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ArgumentBuilderStressTestPage }))
)
const CreativeContextPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.CreativeContextPage }))
)
const CreativeContextSectionLanding = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.CreativeContextSectionLanding }))
)
const FoundationsALOPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsALOPage }))
)
const FoundationsCommunicationBriefsListPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsCommunicationBriefsListPage }))
)
const FoundationsCommunicationBriefPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsCommunicationBriefPage }))
)
const FoundationsFoundationalSymbolsPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsFoundationalSymbolsPage }))
)
const FoundationsGoalPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsGoalPage }))
)
const FoundationsMessageStrategyPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsMessageStrategyPage }))
)
const FoundationsValuePropsPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsValuePropsPage }))
)
const FoundationsSectionDashboard = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsSectionDashboard }))
)
const FoundationsSectionLanding = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsSectionLanding }))
)
const FoundationsTargetPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsTargetPage }))
)
const FoundationsTFDPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.FoundationsTFDPage }))
)
const PersuasionLabDashboard = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.PersuasionLabDashboard }))
)
const PersuasionLabRunDashboard = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.PersuasionLabRunDashboard }))
)
const ResearchAmplifierCredibilityScoringPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ResearchAmplifierCredibilityScoringPage }))
)
const ResearchAmplifierDataMinerPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ResearchAmplifierDataMinerPage }))
)
const ResearchAmplifierExportPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ResearchAmplifierExportPage }))
)
const ResearchAmplifierInsightDistillerPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ResearchAmplifierInsightDistillerPage }))
)
const ResearchAmplifierOverviewPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ResearchAmplifierOverviewPage }))
)
const ResearchAmplifierPatternFinderPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ResearchAmplifierPatternFinderPage }))
)
const ResearchAmplifierQuoteExtractorPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ResearchAmplifierQuoteExtractorPage }))
)
const ResearchAmplifierSectionLanding = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ResearchAmplifierSectionLanding }))
)
const ResearchAmplifierSourceCapturePage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.ResearchAmplifierSourceCapturePage }))
)
const StorySectionLanding = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.StorySectionLanding }))
)
const StoryHubPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.StoryHubPage }))
)
const StoryStudioPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.StoryStudioPage }))
)
const EnhancedBriefPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.EnhancedBriefPage }))
)
const StoryboardStudioPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.StoryboardStudioPage }))
)
const PersuasionCoachPage = lazy(() =>
    import('@/modules/persuasion-lab/pages').then(m => ({ default: m.PersuasionCoachPage }))
)

// ---------------------------------------------------------------------------
// Plans, SEO, settings
// ---------------------------------------------------------------------------
const Plans = lazy(() => import('@/modules/plans/plans'))
const SubscriptionCallback = lazy(() => import('@/modules/plans/subscription-callback'))
const UsageDashboard = lazy(() => import('@/modules/settings/components/UsageDashboard'))
const SettingsRoot = lazy(() => import('@/modules/settings/settingsRoot'))
const Business = lazy(() => import('@/modules/settings/tabs/Business'))
const Personal = lazy(() => import('@/modules/settings/tabs/Personal'))
const Workspaces = lazy(() => import('@/modules/settings/tabs/Workspaces'))

// ---------------------------------------------------------------------------
// Misc app sections
// ---------------------------------------------------------------------------
const SharedFiles = lazy(() => import('@/modules/shared/Shared'))
const Structures = lazy(() => import('@/modules/structures/Structures'))
const ManageCustomStructures = lazy(() => import('@/modules/structures/components/ManageCustomStructures'))
const SupportPage = lazy(() => import('@/modules/support/SupportPage'))

// ---------------------------------------------------------------------------
// Tools (named exports)
// ---------------------------------------------------------------------------
const BrandAssetLibrary = lazy(() =>
    import('@/modules/tools/pages/BrandAssetLibrary').then(m => ({ default: m.BrandAssetLibrary }))
)
const ImageStudio = lazy(() =>
    import('@/modules/tools/pages/ImageStudio').then(m => ({ default: m.ImageStudio }))
)
const ProductStudio = lazy(() =>
    import('@/modules/tools/pages/ProductStudio').then(m => ({ default: m.ProductStudio }))
)

// ---------------------------------------------------------------------------
// Visual Engine (named exports)
// ---------------------------------------------------------------------------
const PhDStepPage = lazy(() =>
    import('@/modules/visual-engine/pages/PhDStepPage').then(m => ({ default: m.PhDStepPage }))
)
const VisualEngineDashboard = lazy(() =>
    import('@/modules/visual-engine/pages/VisualEngineDashboard').then(m => ({ default: m.VisualEngineDashboard }))
)
const VisualEngineDebugPage = lazy(() =>
    import('@/modules/visual-engine/pages/VisualEngineDebugPage').then(m => ({ default: m.VisualEngineDebugPage }))
)
const VisualEngineApiSettingsDashboard = lazy(() =>
    import('@/modules/visual-engine/pages/VisualEngineApiSettingsDashboard').then(m => ({
        default: m.VisualEngineApiSettingsDashboard,
    }))
)
const VisualEngineApiSettingsRun = lazy(() =>
    import('@/modules/visual-engine/pages/VisualEngineApiSettingsRun').then(m => ({ default: m.VisualEngineApiSettingsRun }))
)

// ---------------------------------------------------------------------------
// Website builder, wizards
// ---------------------------------------------------------------------------
const UserWizard = lazy(() => import('@/modules/wizards/user-wizard'))
const Wizard = lazy(() => import('@/modules/wizards/wizard'))

// ---------------------------------------------------------------------------
// Influencer / messaging workflows (named exports)
// ---------------------------------------------------------------------------
const InfluencerBriefWizard = lazy(() =>
    import('@/modules/influencer-brief/InfluencerBriefWizard').then(m => ({ default: m.InfluencerBriefWizard }))
)
const MessagingCampaignWizard = lazy(() =>
    import('@/modules/messaging-campaign/MessagingCampaignWizard').then(m => ({ default: m.MessagingCampaignWizard }))
)

// ---------------------------------------------------------------------------
// Public pages (marketing / freebie)
// ---------------------------------------------------------------------------
const FreebieConfirmed = lazy(() => import('@/pages/FreebieConfirmed'))
const FreebieDecisions = lazy(() => import('@/pages/FreebieDecisions'))
const FreebieDownload = lazy(() => import('@/pages/FreebieDownload'))
const Success = lazy(() => import('@/pages/Success'))
const AboutUs = lazy(() => import('@/pages/about'))
const Contact = lazy(() => import('@/pages/contact'))
const FreeTrial = lazy(() => import('@/pages/free-trial'))
const Pricing = lazy(() => import('@/pages/pricing'))
const ProductFeatures = lazy(() => import('@/pages/product-features'))
const Services = lazy(() => import('@/pages/services'))
const Support = lazy(() => import('@/pages/support'))
const Subscribe = lazy(() => import('@/pages/Subscribe'))

// ---------------------------------------------------------------------------
// Staging new modules
// ---------------------------------------------------------------------------
const AgentPage = lazy(() => import('@/modules/agent/AgentPage'))
const PromptsPage = lazy(() => import('@/modules/prompts').then(m => ({ default: m.PromptsPage })))
const TwoHQRoot = lazy(() => import('@/modules/two-hq/TwoHQRoot'))
const ContentSheetsRoot = lazy(() => import('@/modules/content-sheets/ContentSheetsRoot'))
const ContentSheetEditorPage = lazy(() =>
    import('@/modules/content-sheets/ContentSheetEditorPage').then(m => ({ default: m.ContentSheetEditorPage }))
)
const NewContentSheetPage = lazy(() =>
    import('@/modules/content-sheets/ContentSheetEditorPage').then(m => ({ default: m.NewContentSheetPage }))
)
const PortalLayout = lazy(() => import('@/modules/asset-library/PortalLayout'))
const BrowsePage = lazy(() => import('@/modules/asset-library/pages/BrowsePage'))
const AssetsPage = lazy(() => import('@/modules/asset-library/pages/AssetsPage'))
const UploadPage = lazy(() => import('@/modules/asset-library/pages/UploadPage'))
const GalleriesPage = lazy(() => import('@/modules/asset-library/pages/GalleriesPage'))
const IntegrationsPage = lazy(() => import('@/modules/asset-library/pages/IntegrationsPage'))
const DeveloperPage = lazy(() => import('@/modules/asset-library/pages/DeveloperPage'))
const AdminClientPortals = lazy(() =>
    import('@/modules/admin/client-portals').then(m => ({ default: m.AdminClientPortals }))
)
const TwoHQSettings = lazy(() => import('@/modules/settings/tabs/TwoHQ'))
const Integrations = lazy(() => import('@/modules/settings/tabs/Integrations'))
const SettingsExport = lazy(() => import('@/modules/settings/tabs/Export'))

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------
const AppRoutes = () => {
    return (
        <Suspense fallback={<LoadingSpinner />}>
            <Routes>
                <Route path='/' element={<PublicRouteWrapper component={<Login />} />} />
                <Route path='/login' element={<PublicRouteWrapper component={<Login />} />} />
                <Route path='/logout' element={<LogoutUser />} />
                <Route path='/forgot-password' element={<PublicRouteWrapper component={<ForgotPassword />} />} />
                <Route path='/reset-password' element={<PublicRouteWrapper component={<ResetPassword />} />} />
                <Route path='/set-password' element={<SetPassword />} />
                <Route path='/register' element={<PublicRouteWrapper component={<Register />} />} />
                <Route path='/setup-demo' element={<PublicRouteWrapper component={<SetupDemo />} />} />
                <Route path='/accept-invitation' element={<AcceptInvitation />} />
                <Route path='/verify-email' element={<VerifyEmail />} />

                {/* Magic link login */}
                <Route path='/magic/:token' element={<MagicLinkLogin />} />

                <Route path='/product-features' element={<ProductFeatures />} />
                <Route path='/services' element={<Services />} />
                <Route path='/pricing' element={<Pricing />} />
                <Route path='/about' element={<AboutUs />} />
                <Route path='/trial' element={<FreeTrial />} />
                <Route path='/support' element={<Support />} />
                <Route path='/contact' element={<Contact />} />
                <Route
                    path='/subscribe'
                    element={<PrivateRouteWrapper component={<EmailVerifiedRouteWrapper component={<Subscribe />} />} />}
                />

                <Route path='/freebie/decisions' element={<FreebieDecisions />} />
                <Route path='/freebie/confirmed' element={<FreebieConfirmed />} />
                <Route path='/freebie/:runId' element={<FreebieDownload />} />
                <Route path='/target-report' element={<TargetGroupReport />} />
                <Route
                    path='/magic-execution/:slug/:id'
                    element={
                        <div className='min-h-screen m-0 overflow-hidden bg-white'>
                            <MyFlowExecutionPage />
                        </div>
                    }
                />

                {/* Client Portal */}
                <Route path='/portal/:token' element={<PortalLayout />}>
                    <Route index element={<BrowsePage />} />
                    <Route path='browse' element={<BrowsePage />} />
                    <Route path='browse/:collectionId' element={<BrowsePage />} />
                    <Route path='assets' element={<AssetsPage />} />
                    <Route path='upload' element={<UploadPage />} />
                    <Route path='galleries' element={<GalleriesPage />} />
                    <Route path='integrations' element={<IntegrationsPage />} />
                    <Route path='developer' element={<DeveloperPage />} />
                </Route>

                {/* Public expert interview — no auth required */}
                <Route path='/interview/:token' element={<PublicInterviewPage />} />

                {/* Mobile deep links (universal link fallback) — no auth required */}
                <Route path='/mobile/interview/:sessionId' element={<MobileInterviewDeepLinkPage />} />
                <Route
                    path='/mobile/interview/:sessionId/deliverables'
                    element={<MobileInterviewDeliverablesDeepLinkPage />}
                />

                {/* Companion presentation viewer — no auth required */}
                <Route path='/live/:token' element={<CompanionViewPage />} />

                <Route path='/submit-form' element={<FormSubmission />} />
                <Route path='/stripe-success' element={<SubscriptionCallback />} />
                <Route path='/stripe-cancel' element={<SubscriptionCallback />} />
                <Route path='/success' element={<PublicRouteWrapper component={<Success />} />} />

                <Route path='new-business' element={<PrivateRouteWrapper component={<NewBusiness />} />} />
                <Route
                    path='/dashboard'
                    element={
                        <PrivateRouteWrapper
                            component={
                                <EmailVerifiedRouteWrapper
                                    component={<SubscriptionRouteWrapper component={<DashboardLayout />} />}
                                />
                            }
                        />
                    }
                >
                    <Route index element={<PipelineDashboard />} />
                    <Route path='home' element={<PipelineDashboard />} />
                    <Route path='mobile-consumer/deliverable/:deliverableId' element={<MobileConsumerDeliverableLandingPage />} />
                    <Route path='playbooks' element={<PlaybooksPage />} />
                    {/* Legacy redirects */}
                    <Route path='billing' element={<Navigate to='/dashboard/settings/billing' replace />} />
                    <Route path='usage' element={<Navigate to='/dashboard/settings/billing' replace />} />
                    <Route path='admin' element={<AdminRoute component={<AdminDashboard />} />}>
                        <Route index element={<AdminHome />} />
                        <Route path='users' element={<AdminUsers />} />
                        <Route path='businesses' element={<AdminBusinesses />} />
                        <Route path='freebies' element={<AdminFreebies />} />
                        <Route path='freebies/audit' element={<FreebieRunAuditPage />} />
                        <Route path='freebies/audit/:runId' element={<FreebieRunAuditPage />} />
                        <Route path='prompts' element={<Prompts selectPrompt={() => {}} />} />
                        <Route path='structures' element={<Templates />} />
                        <Route path='structures/:structure' element={<Template />} />
                        <Route path='templates/new' element={<Template />} />
                        <Route path='section-types' element={<SectionTypesList />} />
                        <Route path='section-types/:code' element={<SectionTypeDetail />} />
                        <Route path='entities' element={<AdminFlowEntities />} />
                        <Route path='entities/new' element={<AdminFlowEntity />} />
                        <Route path='entities/:entity' element={<AdminFlowEntity />} />
                        <Route path='wizard-categories' element={<AdminWizardCategories />} />
                        <Route path='wizard-categories/new' element={<AdminWizardCategory />} />
                        <Route path='wizard-categories/:category' element={<AdminWizardCategory />} />
                        <Route path='sequences' element={<AdminSequences />} />
                        <Route path='wizard-forms/wizard-form-builder' element={<AdminWizardFormsBuilder />} />
                        <Route path='wizard-forms/:id' element={<MultiStepFormBuilder />} />
                        <Route path='bundles' element={<AdminBundles />} />
                        <Route path='bundles/new' element={<AdminBundle />} />
                        <Route path='bundles/:bundleId' element={<AdminBundle />} />
                        <Route path='dataset-templates' element={<AdminDatasetTemplates />} />
                        <Route path='dataset-templates/new' element={<AdminDatasetTemplate />} />
                        <Route path='dataset-templates/:template' element={<AdminDatasetTemplate />} />
                        <Route path='comzone-codes' element={<AdminComzoneCodes />} />
                        <Route path='comzone-codes/new' element={<AdminComzoneCode />} />
                        <Route path='comzone-codes/:id' element={<AdminComzoneCode />} />
                        <Route path='interviews' element={<InterviewPacksAdmin />} />
                        <Route path='outcome-catalog' element={<OutcomeCatalogAdmin />} />
                        <Route path='audit/*' element={<AdminAudit />} />
                        <Route path='interview-variables' element={<InterviewVariablesAdmin />} />
                        <Route path='identity-registry' element={<IdentityRegistryAdmin />} />
                        <Route path='deliverable-templates' element={<DeliverableTemplatesAdmin />} />
                        <Route path='deliverable-studio-audit' element={<DeliverableStudioAudit />} />
                        <Route path='mobile-packages' element={<AppStoreSetupAdmin />} />
                        <Route path='mobile-users' element={<MobileUsersAdmin />} />
                        <Route path='credit-audit' element={<CreditAuditAdmin />} />
                        <Route path='mobile-bundles' element={<BundleManagerAdmin />} />
                        <Route path='campaign-scenarios' element={<CampaignScenariosAdmin />} />
                        <Route path='phd-prompts' element={<PhDPromptManagement />} />
                        <Route path='seed-images' element={<SeedImageGallery />} />
                        <Route path='client-portals' element={<AdminClientPortals />} />
                        <Route path='ga-report-templates' element={<AdminGAReportTemplates />} />
                    </Route>
                    <Route path='admin/sequences/new' element={<AdminSequence />} />
                    <Route path='admin/sequences/:slug' element={<AdminSequence />} />
                    <Route path='brand-guide' element={<BrandGuides />} />
                    <Route path='brand-guide/create' element={<ManageBrandGuide />} />
                    <Route path='brand-guide/:id' element={<ManageBrandGuide />} />
                    <Route path='wizards/:id' element={<Wizard />} />
                    <Route path='user-wizards/:id' element={<UserWizard />} />
                    <Route path='documents' element={<Folders />} />
                    <Route path='document/:folder' element={<Folder />} />
                    <Route path='file/:file' element={<DocumentEditorContainer />} />
                    <Route path=':type/file/:file' element={<DocumentEditorContainer />} />
                    <Route path='node/:file' element={<DocumentEditorContainer />} />
                    <Route path='editor/:file' element={<DocumentEditorContainer />} />
                    <Route path='forms' element={<FormRoot />} />
                    <Route path='forms/form' element={<Form />} />
                    <Route path='forms/:form/submissions' element={<CompletedForms />} />
                    <Route path='forms/:form/submissions/:id' element={<CompletedFormResponse />} />
                    <Route path='training' element={<ComingSoon />} />
                    <Route path='brand-voice' element={<BrandVoices />} />
                    <Route index path='templates' element={<Structures />} />
                    <Route index path='templates/create-custom' element={<ManageCustomStructures />} />
                    <Route index path='templates/custom/:id' element={<ManageCustomStructures />} />
                    <Route index path='tools' element={<ImageStudio />} />
                    <Route
                        path='tools'
                        element={<Navigate to='/dashboard/campaigns/visual-studios/image-studio' replace />}
                    />
                    <Route
                        path='tools/image-studio'
                        element={<Navigate to='/dashboard/campaigns/visual-studios/image-studio' replace />}
                    />
                    <Route
                        path='tools/product-studio'
                        element={<Navigate to='/dashboard/campaigns/visual-studios/product-studio' replace />}
                    />
                    <Route path='tools/assets' element={<BrandAssetLibrary />} />
                    <Route path='campaigns/visual-studios/image-studio' element={<ImageStudio />} />
                    <Route path='campaigns/visual-studios/product-studio' element={<ProductStudio />} />
                    <Route index path='media' element={<MediaAssets />} />
                    <Route index path='media/:folder' element={<MediaFolder />} />
                    <Route path='settings' element={<SettingsRoot />}>
                        <Route index path='personal' element={<Personal />} />
                        <Route path='business' element={<PrivateRouteWrapper component={<Business />} />} />
                        <Route path='workspaces' element={<PrivateRouteWrapper component={<Workspaces />} />} />
                        <Route path='billing' element={<PrivateRouteWrapper component={<UsageDashboard />} />} />
                        <Route path='usage' element={<PrivateRouteWrapper component={<UsageDashboard />} />} />
                        <Route path='integrations' element={<Integrations />} />
                        <Route path='2hq' element={<PrivateRouteWrapper component={<TwoHQSettings />} />} />
                        <Route path='export' element={<SettingsExport />} />
                    </Route>
                    <Route path='agent' element={<AgentPage />} />
                    <Route path='prompts' element={<PromptsPage />} />
                    <Route path='2hq' element={<TwoHQRoot />} />
                    <Route path='2hq/report-templates' element={<TwoHQReportTemplatesPage />} />
                    <Route path='2hq/ga-imports' element={<TwoHQGAImportsPage />} />
                    <Route path='2hq/reports' element={<TwoHQReportsPage />} />
                    {/* Legacy redirect: old /dashboard/seo → /dashboard/2hq */}
                    <Route path='seo' element={<Navigate to='/dashboard/2hq' replace />} />
                    <Route path='content-sheets' element={<ContentSheetsRoot />} />
                    <Route path='content-sheets/new' element={<NewContentSheetPage />} />
                    <Route path='content-sheets/:id' element={<ContentSheetEditorPage />} />
                    <Route path='flows/my-workflows' element={<MyWorkflows />} />
                    <Route path='flows/results' element={<Results />} />
                    <Route path='flows/store' element={<WizardStore />} />
                    <Route path='flows/store/cart' element={<Cart />} />
                    <Route path='flows/store/checkout' element={<Checkout />} />
                    <Route path='flows/:slug/:id' element={<MyFlowExecutionPage />} />
                    <Route path='flows/:slug/:id/continue/:executionId' element={<MyFlowExecutionPage />} />
                    <Route path='flows/store/:id' element={<WizardDetailsPage />} />
                    <Route path='flows/my-flows/:id' element={<ExecutionDetailsPage />} />
                    <Route path='visual-engine' element={<VisualEngineDashboard />} />
                    <Route path='visual-engine/runs/:runId/:stepId' element={<PhDStepPage />} />
                    <Route path='visual-engine/runs/:runId/debug' element={<VisualEngineDebugPage />} />
                    <Route path='visual-engine/api-settings' element={<VisualEngineApiSettingsDashboard />} />
                    <Route
                        path='visual-engine/api-settings/:runId/policy'
                        element={<VisualEngineApiSettingsRun mode='policy' />}
                    />
                    <Route
                        path='visual-engine/api-settings/:runId/control-panel'
                        element={<VisualEngineApiSettingsRun mode='control-panel' />}
                    />
                    <Route
                        path='visual-engine/api-settings/:runId/assets'
                        element={<VisualEngineApiSettingsRun mode='assets' />}
                    />

                    {/* Brand Strategy (formerly Persuasion Lab Foundations): Dashboard of runs, click into brand-strategy/:runId → sub-nav */}
                    <Route path='persuasion-lab' element={<PersuasionLabDashboard />} />
                    <Route path='brand-strategy/foundations' element={<FoundationsSectionLanding />} />
                    {/* Portrait portal (Construction) */}
                    <Route path='portraits' element={<PortraitDashboardPage />} />
                    <Route path='portraits/people' element={<PortraitPeopleIndexPage />} />
                    <Route path='portraits/companies' element={<PortraitCompaniesIndexPage />} />
                    <Route path='portraits/products' element={<PortraitProductsIndexPage />} />

                    {/* Portrait viewer routes (stay inside Portrait portal, not Identity Engine) */}
                    <Route path='portraits/people/new' element={<PortraitPeopleStudioPage />} />
                    <Route path='portraits/companies/new' element={<PortraitCompaniesStudioPage />} />
                    <Route path='portraits/products/new' element={<PortraitProductsStudioPage />} />
                    <Route path='portraits/people/:personId' element={<PersonPortraitPortalPage />} />
                    <Route path='portraits/companies/:companyId' element={<CompanyPortraitPortalPage />} />
                    <Route path='portraits/products/:productId' element={<ProductPortraitPortalPage />} />
                    <Route path='brand-strategy/foundations/:runId' element={<FoundationsRunLayout />}>
                        <Route index element={<FoundationsSectionDashboard />} />
                        <Route path='goal' element={<FoundationsGoalPage />} />
                        <Route path='target' element={<FoundationsTargetPage />} />
                        <Route path='tfd' element={<FoundationsTFDPage />} />
                        <Route path='alo' element={<FoundationsALOPage />} />
                        <Route path='foundational-symbols' element={<FoundationsFoundationalSymbolsPage />} />
                        <Route path='message-strategy' element={<FoundationsMessageStrategyPage />} />
                        <Route path='value-props' element={<FoundationsValuePropsPage />} />
                        <Route path='communication-briefs' element={<FoundationsCommunicationBriefsListPage />} />
                        <Route path='communication-briefs/:briefId' element={<FoundationsCommunicationBriefPage />} />
                    </Route>
                    <Route path='persuasion-lab/argument-builder' element={<SituationsDashboard />} />
                    {/* Creative Context section landing — global Story Assets library */}
                    <Route path='persuasion-lab/creative-context' element={<CreativeContextSectionLanding />} />
                    {/* Global Story Assets profile editor */}
                    <Route path='persuasion-lab/story-assets/:profileId' element={<CreativeContextPage />} />
                    <Route path='persuasion-lab/research' element={<ResearchAmplifierSectionLanding />} />
                    <Route path='persuasion-lab/stories' element={<StorySectionLanding />} />
                    <Route path='persuasion-lab/:runId' element={<PersuasionLabBuildLayout />}>
                        <Route index element={<PersuasionLabRunDashboard />} />
                        <Route path='argument-builder' element={<Navigate to='situations' replace />} />
                        <Route path='argument-builder/situations' element={<ArgumentBuilderSituationsPage />} />
                        <Route path='argument-builder/encounter' element={<ArgumentBuilderEncounterPage />} />
                        <Route path='argument-builder/profile-lock' element={<ArgumentBuilderProfileLockPage />} />
                        <Route path='argument-builder/alo-freeze' element={<ArgumentBuilderALOFreezePage />} />
                        <Route path='argument-builder/thesis' element={<ArgumentBuilderClaimStructurePage />} />
                        <Route path='argument-builder/outcomes' element={<ArgumentBuilderOutcomesPage />} />
                        <Route path='argument-builder/outcomes/:outcomeId/brief' element={<EnhancedBriefPage />} />
                        <Route path='argument-builder/objections' element={<ArgumentBuilderObjectionsPage />} />
                        <Route path='argument-builder/pillars' element={<ArgumentBuilderPillarsPage />} />
                        <Route path='argument-builder/stress-test' element={<ArgumentBuilderStressTestPage />} />
                        <Route path='argument-builder/lock-case' element={<ArgumentBuilderLockCasePage />} />
                        <Route path='research' element={<ResearchAmplifierOverviewPage />} />
                        <Route path='research/source-capture' element={<ResearchAmplifierSourceCapturePage />} />
                        <Route path='research/quote-extractor' element={<ResearchAmplifierQuoteExtractorPage />} />
                        <Route path='research/data-miner' element={<ResearchAmplifierDataMinerPage />} />
                        <Route
                            path='research/credibility-scoring'
                            element={<ResearchAmplifierCredibilityScoringPage />}
                        />
                        <Route path='research/insight-distiller' element={<ResearchAmplifierInsightDistillerPage />} />
                        <Route path='research/pattern-finder' element={<ResearchAmplifierPatternFinderPage />} />
                        <Route path='research/export' element={<ResearchAmplifierExportPage />} />
                        <Route path='stories' element={<StoryHubPage />} />
                        <Route path='stories/:storyId' element={<StoryStudioPage />} />
                        <Route path='stories/:storyId/storyboards/:storyboardId' element={<StoryboardStudioPage />} />
                        <Route path='stories/:storyId/persuasion-boards/:boardId' element={<PersuasionBoardDetailPage />} />
                        <Route path='stories/:storyId/persuasion-boards/:boardId/strengthen' element={<PersuasionCoachPage />} />
                        {/* Creative Context — upstream stage before Story Design and Storyboard generation */}
                        <Route path='creative-context' element={<CreativeContextPage />} />
                    </Route>

                    {/* Assets routes */}
                    <Route path='assets/targets' element={<TargetLibrary />} />
                    <Route path='assets/goals' element={<GoalLibrary />} />
                    <Route path='assets/claims' element={<ClaimsLibrary />} />
                    <Route path='assets/tfds' element={<TFDLibrary />} />
                    <Route path='assets/visual-profiles' element={<VisualProfilesLibrary />} />
                    <Route path='assets/evidence' element={<EvidenceLibrary />} />
                    <Route path='assets/companies/setup' element={<IdentityEngineSetupWizard />} />
                    <Route path='assets/companies' element={<LibraryCompaniesPage />} />
                    <Route path='assets/companies/:companyId' element={<CompanyDetailPage />} />
                    <Route path='assets/brand-assets' element={<LibraryBrandAssetsPage />} />
                    <Route path='assets/products' element={<LibraryProductsPage />} />
                    <Route path='assets/products/:productId' element={<ProductDetailPage />} />
                    <Route path='assets/people' element={<LibraryPeoplePage />} />
                    <Route path='assets/people/:personId' element={<PersonDetailPage />} />
                    <Route path='assets/people/:personId/portrait' element={<PersonPortraitPortalPage />} />
                    <Route path='assets/companies/:companyId/portrait' element={<CompanyPortraitPortalPage />} />
                    <Route path='assets/products/:productId/portrait' element={<ProductPortraitPortalPage />} />

                    {/* HEFT Profiles */}
                    <Route path='assets/heft' element={<HeftProfilesPage />} />
                    <Route path='assets/heft/:subjectType/:subjectId/targets/:targetId' element={<HeftTargetSheetPage />} />
                    <Route path='assets/heft/:subjectType/:subjectId' element={<HeftSubjectPage />} />

                    {/* Interview Packs */}
                    <Route path='interview-packs' element={<InterviewPacksPage />} />
                    <Route path='interview-packs/:id' element={<InterviewPackEditor />} />
                    <Route path='interview/strategy/:packId' element={<ContentStrategyPackPage />} />

                    {/* Persona Builder */}
                    <Route path='persona-builder' element={<PersonaBuilderPage />} />
                    <Route path='persona-builder/:subjectType' element={<PersonaBuilderPage />} />
                    <Route path='persona-builder/:subjectType/:subjectId' element={<PersonaBuilderPage />} />

                    {/* Campaigns routes - wrapped with CampaignsProvider */}
                    <Route
                        path='campaigns/*'
                        element={
                            <CampaignsProvider>
                                <Routes>
                                    <Route index element={<CampaignsDashboard />} />
                                    <Route path='new' element={<CreateCampaignPage />} />
                                    <Route path='utilities' element={<Navigate to='studio' replace />} />
                                    <Route path='utilities/studio' element={<DeliverableStudioPage />} />
                                    {/* Single-segment :id must come before :campaignId so /dashboard/campaigns/123 shows overview, not topics (narrative/landing_page don't use topics the same way) */}
                                    <Route path=':id' element={<CampaignDetail />} />
                                    <Route path=':campaignId/foundation' element={<FoundationSummaryPage />} />
                                    {/* Legacy strategy pages — hidden from nav but accessible for reference */}
                                    <Route path=':campaignId/strategy' element={<StrategyPage />} />
                                    <Route path=':campaignId/persona-builder' element={<PersonaBuilderPage />} />
                                    <Route
                                        path=':campaignId/persona-builder/:subjectType'
                                        element={<PersonaBuilderPage />}
                                    />
                                    <Route
                                        path=':campaignId/persona-builder/:subjectType/:subjectId'
                                        element={<PersonaBuilderPage />}
                                    />
                                    <Route path=':campaignId/targets' element={<TargetSelection />} />
                                    <Route path=':campaignId/targets/:targetId/edit' element={<TargetEdit />} />
                                    <Route path=':campaignId/tfd-builder' element={<ThinkFeelDoBuilder />} />
                                    <Route path=':campaignId/landing-page/goals' element={<LandingPageGoalsPage />} />
                                    <Route path=':campaignId/landing-page/visual' element={<LandingPageVisualPage />} />
                                    <Route
                                        path=':campaignId/landing-page/content'
                                        element={<LandingPageContentPage />}
                                    />
                                    <Route
                                        path=':campaignId/landing-page/sections'
                                        element={<Navigate to='../content' replace />}
                                    />
                                    <Route
                                        path=':campaignId/landing-page/copy-lab'
                                        element={<Navigate to='../content' replace />}
                                    />
                                    <Route
                                        path=':campaignId/landing-page/preview'
                                        element={<LandingPagePreviewPage />}
                                    />
                                    <Route path=':campaignId/brand-guidelines' element={<BrandGuidelines />} />
                                    <Route path=':campaignId/strategy-review' element={<StrategyReview />} />
                                    <Route path=':campaignId/alos' element={<ALOInventory />} />
                                    <Route path=':campaignId/assets' element={<AssetsBuilder />} />
                                    <Route path=':campaignId/bait' element={<BaitBuilder />} />
                                    <Route path=':campaignId/corridor' element={<CorridorBuilder />} />
                                    <Route path=':campaignId/matrix' element={<CreativeMatrix />} />
                                    <Route path=':campaignId/dataset' element={<DatasetManager />} />
                                    <Route path=':campaignId/mapping' element={<FieldMapper />} />
                                    <Route path=':campaignId/hooks' element={<HookLibrary />} />
                                    <Route path=':campaignId/intent' element={<IntentDiscovery />} />
                                    <Route path=':campaignId/story-library' element={<PRStoryLibrary />} />
                                    <Route path=':campaignId/newsroom' element={<Newsroom />} />
                                    <Route path=':campaignId/outreach' element={<Outreach />} />
                                    <Route path=':campaignId/pillars' element={<PillarBuilder />} />
                                    <Route path=':campaignId/variations' element={<VariationGenerator />} />
                                    <Route path=':campaignId/voice' element={<VoiceDialIn />} />
                                    <Route path=':campaignId/your-story' element={<YourStory />} />
                                    <Route path=':campaignId/story-architecture' element={<StoryArchitecture />} />
                                    <Route path=':campaignId/strategic-overview' element={<StrategicOverview />} />

                                    <Route path=':campaignId/story-map' element={<StoryMap />} />
                                    <Route path=':campaignId/story-readback' element={<StoryReadbackPage />} />
                                    <Route path=':campaignId/command-center' element={<CampaignCommandCenter />} />
                                    <Route path=':campaignId/debug' element={<CampaignDebugPage />} />
                                    <Route path=':campaignId/audit' element={<CampaignAuditPage />} />
                                    <Route path=':campaignId/framework-canvas' element={<FrameworkCanvas />} />

                                    {/* Asset Fan-out Routes */}
                                    <Route path=':campaignId/mega-asset' element={<MegaAssetBuilder />} />
                                    <Route path=':campaignId/fan-out' element={<FanOutGenerator />} />
                                    <Route path=':campaignId/scheduling' element={<DripScheduler />} />

                                    {/* Influencer Campaign Routes — DEPRECATED legacy flow (script-builder-first).
                                         * Retained for read-only compatibility with existing campaigns.
                                         * Do not add new features to these routes.
                                         * New influencer campaigns use influencer_v2 (storyboard-first) below. */}
                                    <Route path=':campaignId/script-builder' element={<ScriptBuilder />} />
                                    <Route path=':campaignId/storyboard' element={<Storyboard />} />
                                    {/* brief-package shared between legacy influencer and influencer_v2 */}
                                    <Route path=':campaignId/brief-package' element={<BriefPackage />} />
                                    {/* Influencer Campaign Routes — CANONICAL influencer_v2 (storyboard-first / Brief Source) flow.
                                         * This is the intended default for all new influencer campaigns.
                                         * Upstream storyboard is authored in Persuasion Lab; execution adapts it here. */}
                                    <Route path=':campaignId/import-storyboard' element={<ImportStoryboardPage />} />
                                    <Route path=':campaignId/creator-setup' element={<CreatorSetupPage />} />
                                    <Route path=':campaignId/moments' element={<MomentsPage />} />
                                    {/* HEFT Campaign Step Routes */}
                                    <Route path=':campaignId/story-selection' element={<HeftStorySelectionPage />} />
                                    <Route path=':campaignId/identity-snapshot' element={<HeftIdentitySnapshotPage />} />
                                    <Route path=':campaignId/deliverables' element={<HeftDeliverablesPage />} />
                                    <Route path=':campaignId/case-study' element={<HeftCaseStudyPage />} />
                                    {/* HEFT Track B Routes */}
                                    <Route path=':campaignId/content-strategy' element={<HeftContentStrategyPage />} />
                                    <Route path=':campaignId/beacons' element={<HeftBeaconsPage />} />
                                    <Route path=':campaignId/tfd' element={<HeftTFDPage />} />
                                    <Route path=':campaignId/values-messaging' element={<HeftMoralValuesPage />} />
                                    <Route path=':campaignId/pr-deliverables' element={<HeftPRDeliverablesPage />} />
                                    <Route path=':campaignId/recruiting-deliverables' element={<HeftRecruitingDeliverablesPage />} />
                                    <Route path=':campaignId/sales-deliverables' element={<HeftSalesDeliverablesPage />} />
                                    <Route path=':campaignId/star-deep-dive' element={<HeftStarDeepDivePage />} />

                                    {/* Topic & Visual Identity - only match when path has /topics or /visual-identity so /dashboard/campaigns/:id stays as overview (CampaignDetail) */}
                                    <Route path=':campaignId/topics' element={<BeatPageLayout />}>
                                        <Route index element={<TopicBankSimple />} />
                                    </Route>
                                    <Route path=':campaignId/visual-identity' element={<BeatPageLayout />}>
                                        <Route index element={<TopicBuilder />} />
                                    </Route>
                                    <Route path=':campaignId/topic-builder' element={<BeatPageLayout />}>
                                        <Route index element={<TopicBuilder />} />
                                    </Route>
                                </Routes>
                            </CampaignsProvider>
                        }
                    />

                    {/* Influencer Brief Workflow - Used when creating new influencer campaigns */}
                    <Route path='influencer-brief/start' element={<InfluencerBriefWizard />} />

                    {/* Messaging Campaign Workflow - Used when creating new messaging campaigns */}
                    <Route path='messaging-campaign/start' element={<MessagingCampaignWizard />} />

                    <Route path='shared' element={<SharedFiles />} />
                    <Route path='shared/:folder' element={<SharedFiles />} />
                    <Route path='datasets' element={<DatasetsRoot />} />
                    <Route path='datasets/groups/:groupId' element={<DatasetsRoot />} />
                    <Route path='datasets/groups/:groupId/datasets/:id' element={<DatasetDetail />} />
                    <Route path='support' element={<SupportPage />} />
                    <Route path='help' element={<HelpEntryPage />} />
                    <Route path='help/heft' element={<HeftHelpPage />} />
                </Route>
            </Routes>
            <Plans />
        </Suspense>
    )
}

export default AppRoutes
